import type {
    BaseError,
    CartErrorResponse,
    CheckoutPaymentErrorResponse,
    InventoryResponseError,
    LineItemError,
    PaymentResponseError,
} from '@libs/common-node';
import type { DeliveryType } from '@libs/core';
import { HttpStatus } from '@libs/core';

import type { CartValidationErrorDetails } from '../../types';
import { CartValidationErrorKey } from '../../utils/notificationMaps';
import { isCartClientErrorResponse } from './error/cartBadRequestError';

export const unexpectedError = {
    errorCode: 'cart.checkout.unexpectederrorbanner',
    message: 'An unexpected error occurred. Please try again later.',
};

const mapCartBaseErrorToValidationDetails = (cartErr: BaseError): CartValidationErrorDetails => {
    const errorCode =
        cartErr.errorCode === CartValidationErrorKey.AUTOSHIP_NOT_SUPPORTED
            ? CartValidationErrorKey.INELIGIBLE_ITEM
            : cartErr.errorCode;
    const isDiscontinued = errorCode === CartValidationErrorKey.DISCONTINUED_PRODUCT;
    // API may send the display name only on `message`; optional `productName` wins when present.
    const resolvedProductName = cartErr.productName?.trim() || (isDiscontinued ? cartErr.message?.trim() : undefined);
    return {
        errorCode,
        message: cartErr.message,
        ...(resolvedProductName ? { productName: resolvedProductName } : {}),
    };
};

// A loyalty outage reaches the client as a 5xx that still names `cart.loyaltydown` (CPC-8012). That code is what
// raises the loyalty banner and takes down the Treats Rewards components, so it has to outlive the generic 5xx
// fallback below. Returning it alone also keeps the unexpected-error banner off the page.
const findLoyaltyDownError = (errorResponse: CartErrorResponse): CartValidationErrorDetails | undefined => {
    const errors = errorResponse.errors;

    if (!Array.isArray(errors)) {
        return undefined;
    }

    const loyaltyDownError = errors.find(error => error?.errorCode === CartValidationErrorKey.LOYALTY_DOWN);

    return loyaltyDownError ? mapCartBaseErrorToValidationDetails(loyaltyDownError) : undefined;
};

export const parseErrorResponse = (
    cartError?: Maybe<CartErrorResponse> | (Response & { parsedResponse: CartErrorResponse }),
    attemptedFulfillmentMethod?: DeliveryType
): CartValidationErrorDetails[] => {
    // "400 - Bad request" case with 'Validation Error'
    if (!cartError || typeof cartError === 'string') {
        return [unexpectedError];
    }

    const errorResponse = 'parsedResponse' in cartError ? cartError.parsedResponse : cartError;

    // Handle 5xx errors explicitly
    if (
        errorResponse?.status >= HttpStatus.INTERNAL_SERVER_ERROR &&
        errorResponse?.status < HttpStatus.NETWORK_AUTHENTICATION_REQUIRED
    ) {
        return [findLoyaltyDownError(errorResponse) ?? unexpectedError];
    }

    // 4xx - Client errors based on https://petsmart.atlassian.net/wiki/spaces/DPT1/pages/770050236/Cart+API+Definition+V3#Error-response
    if (isCartClientErrorResponse(errorResponse)) {
        const { errors } = errorResponse;

        return (
            errors?.map(error => {
                // Map cart.checkout.autoship.notSupported to INELIGIBLE_ITEM for consistent handling
                const errorCode =
                    error.errorCode === CartValidationErrorKey.AUTOSHIP_NOT_SUPPORTED
                        ? CartValidationErrorKey.INELIGIBLE_ITEM
                        : error.errorCode || unexpectedError.errorCode;
                return {
                    errorCode,
                    message: error.message || unexpectedError.message,
                };
            }) || [unexpectedError]
        );
    }

    // 422 Cart validation case: https://petsmart.atlassian.net/wiki/spaces/DPT1/pages/770050236/Cart+API+Definition+V3#Validation-response
    const errors = errorResponse?.errors;

    // Place order and some endpoints return `errors` as BaseError[] instead of { cartErrors, lineErrors }.
    if (Array.isArray(errors)) {
        const mapped = errors.map(mapCartBaseErrorToValidationDetails);
        return mapped.length > 0 ? mapped : [unexpectedError];
    }

    const cartErrors = errors?.cartErrors?.map(mapCartBaseErrorToValidationDetails) || [];

    const responseInventoryErrors = Array.isArray(errorResponse?.inventory_errors)
        ? errorResponse?.inventory_errors
        : [errorResponse?.inventory_errors];

    const responsePaymentErrors = Array.isArray(errorResponse?.payment_errors)
        ? errorResponse?.payment_errors
        : [errorResponse?.payment_errors];

    // Check for inventory_errors in the errorResponse
    const inventoryErrors =
        (responseInventoryErrors as InventoryResponseError[])?.filter(Boolean).map((error: InventoryResponseError) => ({
            lineItemId: error.id,
            errorCode: error.errorCode,
            message: error.message,
            productName: error.productName,
            available: error.available,
            isAttemptedFMTransition: Boolean(attemptedFulfillmentMethod),
        })) || [];

    const paymentErrors =
        (responsePaymentErrors as PaymentResponseError[])?.filter(Boolean).map((error: PaymentResponseError) => ({
            errorCode: error.errorCode ?? unexpectedError.errorCode,
            message: error.errorMessage ?? unexpectedError.message,
        })) || [];

    const { lineItemErrors = [], lineErrors = [] } = errors || {};

    const parsedLineErrors =
        [...lineItemErrors, ...lineErrors].flatMap(
            lineErr =>
                lineErr.lineItemErrors?.map((lineItemErr: LineItemError) => ({
                    lineItemId: lineErr.lineItemId,
                    productName: lineErr.productName,
                    errorCode: lineItemErr.errorCode,
                    message: lineItemErr.message,
                    available: lineItemErr.available,
                })) || []
        ) || [];

    const mergedErrors = [...cartErrors, ...parsedLineErrors, ...inventoryErrors, ...paymentErrors];

    return mergedErrors.length > 0 ? mergedErrors : [unexpectedError];
};

export const parseCheckoutPaymentErrorResponse = (
    checkoutPaymentError?: Maybe<CheckoutPaymentErrorResponse>
): CartValidationErrorDetails[] => checkoutPaymentError?.flatMap(err => err.paymentErrors || []) || [];
